import {
  getKodbegreppInfo,
  kodbegreppEnabled,
  getKodbegrepp,
  KoddelTypes,
  getKoddelByKoddelType,
} from '../KodplanUtils';
import { kodplaner_ekkodplaner } from '../../../Components/KodplanManager/Queries/_QueryTypes/kodplaner';
import { IClientAttributeModel } from '../../../Types/ClientModelTypes';
import { ClientAttributeProperty } from '../../avenyModelUtils';
import { setProperty } from './PropertyUtils';
import Logger, { namedError } from '../../../Utils/Logger';
import type { ISessionInfo } from '../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';

const disableAttribute = (
  attribute: IClientAttributeModel
): IClientAttributeModel => {
  return { ...attribute, Condition: 'Internal', Attribute: 'Internal' };
};

const setAttributeDisplayLabel = (
  displayLabel: string | null,
  attribute: IClientAttributeModel
): IClientAttributeModel => {
  if (displayLabel == null) {
    Logger.warn(
      namedError(
        'UpdateAttributeUtils',
        `No displaylabel for attribute ${attribute.AttributeName}`
      )
    );
    displayLabel = 'UNKNOWN';
  }
  return { ...attribute, DisplayLabel: displayLabel };
};

const updateKontobegreppAttribute = (
  kodplan: kodplaner_ekkodplaner,
  kontobegreppAttribute: IClientAttributeModel
): IClientAttributeModel => {
  const kodbegreppInfo = getKodbegreppInfo(kontobegreppAttribute.AttributeName);

  const enabled = kodbegreppEnabled(kodplan, kodbegreppInfo);

  if (!enabled) {
    return disableAttribute(kontobegreppAttribute);
  }

  const kodbegrepp = getKodbegrepp(kodplan, kodbegreppInfo);

  if (!kodbegrepp) {
    Logger.error(
      `Could not find kodbegrepp for attribute ${kontobegreppAttribute.AttributeName}`
    );
    return disableAttribute(kontobegreppAttribute);
  }

  const koddel = kodplan.koddelar && kodplan.koddelar[kodbegreppInfo.index - 1];

  kontobegreppAttribute = updateAttributeSortOrder(
    kodbegreppInfo.startPosition,
    kontobegreppAttribute
  );
  kontobegreppAttribute = updateGroupSortOrder(
    koddel ? koddel.ordnings_nr : null,
    kontobegreppAttribute
  );
  kontobegreppAttribute = updateAttributeGroup(
    koddel ? koddel.namn : '',
    kontobegreppAttribute
  );
  return setAttributeDisplayLabel(kodbegrepp.namn, kontobegreppAttribute);
};

const updateAttributeGroup = (
  groupName: string | null,
  koddelAttribute: IClientAttributeModel
): IClientAttributeModel => {
  return {
    ...koddelAttribute,
    AttributeGroup: groupName || '',
  };
};

const hardCodedAttributes = {
  REDOVISN_TYP: { groupName: KoddelTypes.Baskonto, attributeSortOrder: 21 },
  KI_TYP: { groupName: KoddelTypes.Baskonto, attributeSortOrder: 22 },
  KB_VERKSOMR: { groupName: KoddelTypes.Verksamhet, attributeSortOrder: 11 },
  KB_VERKSAMHET: { groupName: KoddelTypes.Verksamhet, attributeSortOrder: 12 },
  LONEART: { groupName: 'Personal', groupSortOrder: 11, attributeSortOrder: 1 },
  BEFATTNINGSKOD: {
    groupName: 'Personal',
    groupSortOrder: 11,
    attributeSortOrder: 2,
  },
  MOMS_KOD: { groupName: 'Övrigt', groupSortOrder: 12 },
  EXTERNT_ANSTNR: {
    groupName: 'Personal',
    groupSortOrder: 11,
    attributeSortOrder: 3,
  },
  Konteringskod: { groupName: 'Övrigt', groupSortOrder: 12 },
};

const updateHardCodedAttribute = (
  kodplan: kodplaner_ekkodplaner,
  attribute: IClientAttributeModel
): IClientAttributeModel => {
  const hardCodedAttribute = (
    hardCodedAttributes as Record<string, { groupName: KoddelTypes | string; attributeSortOrder?: number; groupSortOrder?: number }>
  )[attribute.AttributeName];
  const hardCodedGroupName: KoddelTypes | string = hardCodedAttribute.groupName;
  const hardCodedGroupSortOrder: number | undefined = hardCodedAttribute.groupSortOrder;
  const hardCodedAttributeSortOrder: number | undefined = hardCodedAttribute.attributeSortOrder;

  let groupName: string | undefined;
  let groupSortOrder: number | undefined;
  if (typeof hardCodedGroupName === 'string') {
    groupName = hardCodedGroupName;
    if (hardCodedGroupSortOrder) {
      groupSortOrder = hardCodedGroupSortOrder;
    } else {
      Logger.error(
        new Error(
          'hardCodedGroupName is of type string but no groupSortOrder provided'
        )
      );
      groupSortOrder = 12;
    }
  } else {
    const koddel = getKoddelByKoddelType(hardCodedGroupName, kodplan);

    if (koddel && koddel.namn && koddel.ordnings_nr) {
      groupName = koddel.namn;
      groupSortOrder = koddel.ordnings_nr;
    } else {
      groupName = 'Övrigt';
      groupSortOrder = 12;
    }
  }

  attribute = updateAttributeSortOrder(hardCodedAttributeSortOrder, attribute);
  attribute = updateGroupSortOrder(groupSortOrder, attribute);
  return {
    ...attribute,
    AttributeGroup: groupName,
  };
};

const updateGroupSortOrder = (
  sortOrder: number | null,
  attribute: IClientAttributeModel
): IClientAttributeModel => {
  sortOrder = sortOrder != null ? sortOrder : 0;

  return {
    ...attribute,
    Properties: setProperty(
      ClientAttributeProperty.groupSortOrder,
      sortOrder.toString(),
      attribute.Properties
    ),
  };
};

const updateAttributeSortOrder = (
  sortOrder: number | undefined,
  attribute: IClientAttributeModel
): IClientAttributeModel => {
  if (sortOrder == null) {
    return attribute;
  } else {
    return {
      ...attribute,
      Properties: setProperty(
        ClientAttributeProperty.attributeSortOrder,
        sortOrder.toString(),
        attribute.Properties
      ),
    };
  }
};

const updateKoddelAttribute = (
  collectionName: string,
  kodplan: kodplaner_ekkodplaner,
  koddelAttribute: IClientAttributeModel
): IClientAttributeModel => {
  const koddelPropertyValue = koddelAttribute.Properties?.get('Kontodel');

  let koddelNumber: number;
  if (koddelPropertyValue) {
    try {
      koddelNumber = Number.parseInt(koddelPropertyValue, 10);
    } catch (error) {
      Logger.error(error instanceof Error ? error : String(error));
      return koddelAttribute;
    }
  } else {
    Logger.error(
      `Could not read property Kontodel of attribute ${koddelAttribute}`
    );
    return koddelAttribute;
  }

  const koddel = kodplan.koddelar && kodplan.koddelar[koddelNumber - 1];

  if (!koddel) {
    return disableAttribute(koddelAttribute);
  } else {
    if (collectionName === 'ekkonto') {
      koddelAttribute = updateAttributeSortOrder(1, koddelAttribute);
      koddelAttribute = updateGroupSortOrder(
        koddel.ordnings_nr,
        koddelAttribute
      );
      koddelAttribute = updateAttributeGroup(koddel.namn, koddelAttribute);
    }
    return setAttributeDisplayLabel(koddel.namn, koddelAttribute);
  }
};

const isHardCodedAttribute = (attribute: IClientAttributeModel): boolean =>
  Object.keys(hardCodedAttributes).includes(attribute.AttributeName);

export const updateAttribute =
  (
    collectionName: string,
    kodplan: kodplaner_ekkodplaner,
    sessionInfo: ISessionInfo | undefined
  ) =>
  (attribute: IClientAttributeModel): IClientAttributeModel => {
    let newAttribute: IClientAttributeModel;

    if (attribute.Properties?.get('KONTOBEGREPP') === 'Y') {
      newAttribute = updateKontobegreppAttribute(kodplan, attribute);
    } else if (attribute.Properties?.get('Kontodel') != null) {
      newAttribute = updateKoddelAttribute(collectionName, kodplan, attribute);
    } else if (isHardCodedAttribute(attribute)) {
      newAttribute = updateHardCodedAttribute(kodplan, attribute);
    } else if (
      collectionName.toLowerCase() === 'lrfakturadel' &&
      attribute.AttributeName === 'AttesterasAv'
    ) {
      newAttribute = disableAttribute(attribute);
    } else if (attribute.Properties?.get('HIDE_IN_NYA_AVENY') === 'Y') {
      newAttribute = disableAttribute(attribute);
    } else {
      return attribute;
    }

    if (
      attribute.AttributeName === 'EXTERNT_ANSTNR' &&
      !sessionInfo?.UseAnstnr
    ) {
      newAttribute = disableAttribute(attribute);
    } else if (
      attribute.AttributeName === 'LONEART' &&
      !sessionInfo?.UseLoneart
    ) {
      newAttribute = disableAttribute(attribute);
    } else if (
      attribute.AttributeName === 'BEFATTNINGSKOD' &&
      !sessionInfo?.UseBefattningskod
    ) {
      newAttribute = disableAttribute(attribute);
    } else if (
      attribute.AttributeName === 'LONEARTSGRUPP' &&
      !sessionInfo?.UseLoneartsgrupp
    ) {
      newAttribute = disableAttribute(attribute);
    }

    return newAttribute;
  };
